/**
 * Recently Viewed Products
 * ------------------------
 * Tracks product handles visited on PDPs in localStorage and renders them
 * inside the <recently-viewed-products> custom element using the theme's
 * native `card-product` snippet via the Section Rendering API.
 *
 * Storage shape:
 *   key:   the element's data-storage-key (e.g. "rikumo:recently-viewed")
 *   value: JSON array of product handles, most recent first
 */

(function () {
  'use strict';

  const DEFAULT_STORAGE_KEY = 'rikumo:recently-viewed';
  const DEFAULT_MAX_STORED = 20;

  function hasLocalStorage() {
    try {
      const probe = '__rv_probe__';
      window.localStorage.setItem(probe, probe);
      window.localStorage.removeItem(probe);
      return true;
    } catch (_) {
      return false;
    }
  }

  function readList(key) {
    if (!hasLocalStorage()) return [];
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (h) {
        return typeof h === 'string' && h.length > 0;
      });
    } catch (_) {
      return [];
    }
  }

  function writeList(key, list, max) {
    if (!hasLocalStorage()) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(list.slice(0, max)));
    } catch (_) {
      // Quota or serialization failure — silently drop.
    }
  }

  function trackHandle(key, handle, max) {
    if (!handle) return;
    const list = readList(key).filter(function (h) {
      return h !== handle;
    });
    list.unshift(handle);
    writeList(key, list, max);
  }

  function pruneHandles(key, removeSet, max) {
    if (!removeSet.size) return;
    const list = readList(key).filter(function (h) {
      return !removeSet.has(h);
    });
    writeList(key, list, max);
  }

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      this.storageKey = this.dataset.storageKey || DEFAULT_STORAGE_KEY;
      this.maxStored = parseInt(this.dataset.maxStored, 10) || DEFAULT_MAX_STORED;
      this.currentHandle = (this.dataset.currentHandle || '').trim();
      this.limit = Math.max(1, parseInt(this.dataset.limit, 10) || 4);
      this.cardSectionId = this.dataset.cardSectionId;
      this.grid = this.querySelector('[data-recently-viewed-grid]');

      trackHandle(this.storageKey, this.currentHandle, this.maxStored);

      if (!this.grid || !this.cardSectionId) return;
      this.render();
    }

    render() {
      const handles = readList(this.storageKey)
        .filter((h) => h !== this.currentHandle)
        .slice(0, this.limit);

      if (!handles.length) return;

      const routeRoot =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      const requests = handles.map((handle) =>
        this.fetchCard(routeRoot, handle).then((result) => ({ handle, result }))
      );

      const wrapInListItem = this.grid.tagName === 'UL' || this.grid.tagName === 'OL';

      Promise.all(requests).then((entries) => {
        const fragment = document.createDocumentFragment();
        const invalid = new Set();

        entries.forEach(({ handle, result }) => {
          if (result.removed || !result.html) {
            if (result.removed) invalid.add(handle);
            return;
          }
          if (wrapInListItem) {
            const li = document.createElement('li');
            li.className = 'grid__item';
            li.innerHTML = result.html;
            fragment.appendChild(li);
          } else {
            const tmp = document.createElement('div');
            tmp.innerHTML = result.html;
            while (tmp.firstChild) fragment.appendChild(tmp.firstChild);
          }
        });

        if (fragment.childNodes.length) {
          this.grid.appendChild(fragment);
          this.hidden = false;
        }

        pruneHandles(this.storageKey, invalid, this.maxStored);
      });
    }

    fetchCard(routeRoot, handle) {
      const url =
        routeRoot +
        'products/' +
        encodeURIComponent(handle) +
        '?section_id=' +
        encodeURIComponent(this.cardSectionId);

      return fetch(url, { credentials: 'same-origin' })
        .then((res) => {
          if (res.status === 404) return { removed: true };
          if (!res.ok) return { html: null };
          return res.text().then((text) => ({ html: extractSectionInner(text) }));
        })
        .catch(() => ({ html: null }));
    }
  }

  function extractSectionInner(html) {
    if (!html) return null;
    const tmp = document.createElement('div');
    tmp.innerHTML = html.trim();
    const wrapper = tmp.firstElementChild;
    const inner = wrapper ? wrapper.innerHTML.trim() : tmp.innerHTML.trim();
    return inner.length ? inner : null;
  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }
})();
